import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetSalesPoints, dataSetFarms } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { ISalesPoint, SalesPoint } from '../../../@types/model/masterData/salesPoint/salesPoint';
import { ISalesPointFormValues, SalesPointFormValues } from '../../../@types/model/masterData/salesPoint/salesPointFormValues';
import SalesPointHttpService from '../../../services/http/masterData/salesPointHttpService';
import FarmHttpService from '../../../services/http/masterData/farmHttpService';
import SalesPointForm from './form/SalesPointForm';
import { IApiError } from '../../../@types/other';
import { setSalesPointMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const SalesPointScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const salesPoints = useAppSelector(state => state.masterData.salesPoints);
    const farms = useAppSelector(state => state.masterData.farms);
    const auth = useAppSelector(state => state.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedSalesPoint, setSelectedSalesPoint] = useState<ISalesPoint>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingSalesPoint, setDeletingSalesPoint] = useState<ISalesPoint>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const [salesPointRes, farmRes] = await Promise.all([
                        SalesPointHttpService.getSalesPointData(),
                        FarmHttpService.getFarmData(),
                    ]);
                    dispatch(dataSetSalesPoints(salesPointRes.data));
                    dispatch(dataSetFarms(farmRes.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading sales points data.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedSalesPoint(undefined);
        setFormGuid(undefined);
    }, []);

    const editSalesPoint = useCallback((salesPoint : ISalesPoint) => {
        setSelectedSalesPoint(salesPoint);
        setFormGuid(salesPoint.guid);
        setIsDialogOpen(true);
    }, []);

    const formatFarms = useCallback((farmIds : Array<number>) => {
        let string = farms
            .filter(x => farmIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
        if (string.length > 85) {
            string = string.substring(0, 86) + '...';
        }
        return string;
    }, [farms]);

    const onSubmit = useCallback(async (values : ISalesPointFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const res = await SalesPointHttpService.addOrUpdateSalesPoint(SalesPoint(payload));
            const updatedList = upsertArrayElement(salesPoints, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSalesPoints(updatedList));
            await setSalesPointMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedSalesPoint ? 'Sales point updated successfully.' : 'Sales point added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating sales point data.');
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, formGuid, salesPoints, selectedSalesPoint, closeDialog]);

    const openDeleteConfirmationPopup = useCallback((salesPoint : ISalesPoint) => {
        setIsDeletePopupOpen(true);
        setDeletingSalesPoint(salesPoint);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setIsDeletePopupOpen(false);
        setDeletingSalesPoint(undefined);
    }, []);

    const removeSalesPoint = useCallback(async () => {
        if (!deletingSalesPoint) return;
        const updatedSalesPoint = { ...deletingSalesPoint, isActive: false };
        setIsLoading(true);
        try {
            const res = await SalesPointHttpService.deleteSalesPoint(updatedSalesPoint.id);
            const updatedList = upsertArrayElement(salesPoints, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSalesPoints(updatedList));
            await setSalesPointMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Sales point deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting sales point.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    }, [deletingSalesPoint, dispatch, salesPoints, closeDeleteConfirmationPopup]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return SalesPointFormValues(selectedSalesPoint, farms);
        }
    }, [selectedSalesPoint, farms, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth?.session?.user?.rights, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Sales Point'
                    isEdit={!!selectedSalesPoint}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    maxWidth={'md'}
                    fullWidth
                    onClose={closeDialog}>
                    <SalesPointForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<ISalesPoint>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editSalesPoint}
                        enableEditing={hasEditingRight}
                        enableDeleting={salesPoint => salesPoint.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Farms', field: 'farmIds', formatFunction: formatFarms, width: 300, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={salesPoints}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Sales Point'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this sales point?'
                onAccept={removeSalesPoint}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default SalesPointScreen;
